import { useEffect } from 'react';
import { Route, Routes, useNavigate, Navigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import {
    PhoneAuth,
    ProtectedRoute,
    useAccountContext,
    AccountProvider,
    DirectOTPContainer,
} from '@lendica/auth';
import { pushDL } from '@lendica/api';
import { baseURL, env } from './api/axiosInstance';
import { AppContainer } from './AppContainer';
import { SplitFactoryProvider, useSplitTreatments } from '@splitsoftware/splitio-react';
import {
    splitConfig,
    FEATURE_FLAGS,
    getUserKey,
    getUserAttributes,
    getLeadFormComponent,
} from '@lendica/application-form';
import { LeadDataProvider } from '@lendica/application-form';
import { CreatePasswordView } from '@lendica/auth';

// Track redirect attempts to prevent infinite loops
let redirectAttempts = 0;
const MAX_REDIRECT_ATTEMPTS = 3;
const REDIRECT_RESET_TIMEOUT = 10000; // Reset counter after 10 seconds

const PhoneAuthContainer = ({ onAuthSuccess, onAccountClear }) => {
    const { storePortedTokens, clearAccounts } = useAccountContext();
    // const isConfigured = useConfigureAxios();

    // Track login page landing
    useEffect(() => {
        pushDL('auth_step', {
            step: 'login_load',
        });
    }, []);

    const onRequestError = async e => {
        if (e.config?.headers?.['X-Skip-Global-Error'] === 'true') {
            // re‑throw so your component can catch it (or swallow if you prefer)
            return;
        }
        if (e.response && e.response.status >= 400) {
            console.log('onRequestError', e.response.status);

            // Check if we're in a redirect loop
            redirectAttempts++;
            if (redirectAttempts >= MAX_REDIRECT_ATTEMPTS) {
                console.error('Detected potential redirect loop. Stopping redirects.');
                // Clear everything to break the cycle
                await clearAccounts();
                localStorage.clear(); // Nuclear option - clear all localStorage

                // Reset counter after timeout
                setTimeout(() => {
                    redirectAttempts = 0;
                }, REDIRECT_RESET_TIMEOUT);

                // Show error to user instead of redirecting
                alert(
                    'Authentication error detected. Please clear your browser cache and try again.'
                );
                return Promise.resolve('loop-prevented');
            }

            await new Promise(async resolve => {
                await clearAccounts();
                setTimeout(() => resolve('removed!'), 500);
            }).then(() => {
                onAccountClear();
            });

            // Reset counter after successful redirect
            setTimeout(() => {
                redirectAttempts = 0;
            }, REDIRECT_RESET_TIMEOUT);

            return Promise.resolve('cleared');
        }
        return Promise.reject(e);
    };

    useEffect(() => {
        axios.interceptors.response.use(response => response, onRequestError);
    }, []);

    return (
        <PhoneAuth
            baseURL={baseURL}
            onSuccess={async tokens => {
                await storePortedTokens(tokens);
                onAuthSuccess();
            }}
            body="Start or retrieve a secured application with your phone number."
        />
    );
};

const DirectOTPAuthContainer = ({ onAuthSuccess, onAccountClear }) => {
    const { storePortedTokens, clearAccounts } = useAccountContext();

    // Track OTP page landing from lead form
    useEffect(() => {
        pushDL('auth_step', {
            step: 'otp_load_from_lead',
        });
    }, []);

    return (
        <DirectOTPContainer
            onSuccess={async tokens => {
                await storePortedTokens(tokens);
                onAuthSuccess();
            }}
        />
    );
};

const WelcomePageWithSplit = () => {
    // Get search params to pass through to lead forms
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    // Get treatment for the lead form test
    const { treatments, isReady } = useSplitTreatments({
        names: [FEATURE_FLAGS.LEAD_FORM_TEST],
        attributes: getUserAttributes(),
    });

    useEffect(() => {
        // Make pushDL available globally for other components
        window.pushDL = pushDL;

        if (isReady) {
            const treatment = treatments[FEATURE_FLAGS.LEAD_FORM_TEST];

            pushDL('ab_test_step', {
                step: 'variant_shown',
                ab_test_name: FEATURE_FLAGS.LEAD_FORM_TEST,
                ab_test_variant: treatment.treatment,
                page: 'welcome',
                user_id_source: document.cookie.includes('hubspotutk') ? 'hubspot' : 'localStorage',
                split_impression_key: treatment.config?.['impression_key'] || null,
            });
        }
    }, [isReady, treatments]);

    // Show loading while Split is determining treatment
    if (!isReady) {
        return <div>Loading...</div>; // You can replace with a proper loading component
    }

    const treatment = treatments[FEATURE_FLAGS.LEAD_FORM_TEST];

    const handleLeadComplete = async (_, { variant, searchParams: params }) => {
        const preservedParams = params?.toString();
        const queryString = preservedParams ? `?${preservedParams}` : '';
        const variantQuery = variant ? `${queryString ? '&' : '?'}variant=${variant}` : '';
        navigate(`/verify-phone${queryString}${variantQuery}`);
    };

    // Use the treatment mapping from the feature package
    const LeadFormComponent = getLeadFormComponent(treatment.treatment);

    // Pass searchParams, baseURL, and onComplete to the selected component
    return (
        <LeadFormComponent
            searchParams={searchParams}
            onComplete={handleLeadComplete}
            baseURL={baseURL}
            appType="application"
            env={env}
        />
    );
};

const AppWithSplit = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const handleAuthSuccess = () => {
        // Track successful authentication
        pushDL('auth_step', {
            step: 'auth_success',
        });

        navigate(`/application?${searchParams.toString()}`);
    };

    const handleAccountClear = () => {
        // Track account clearing/logout
        pushDL('auth_step', {
            step: 'auth_cleared',
        });

        navigate(`/login?${searchParams.toString()}`);
    };

    return (
        <AccountProvider onAuthSuccess={handleAuthSuccess} onAccountClear={handleAccountClear}>
            <LeadDataProvider>
                <Routes>
                    <Route element={<ProtectedRoute />}>
                        <Route path="*" element={<AppContainer />} />
                    </Route>
                    <Route
                        path="/login/*"
                        element={
                            <PhoneAuthContainer
                                onAuthSuccess={handleAuthSuccess}
                                onAccountClear={handleAccountClear}
                            />
                        }
                    />
                    <Route
                        path="/verify-phone/*"
                        element={
                            <DirectOTPAuthContainer
                                onAuthSuccess={handleAuthSuccess}
                                onAccountClear={handleAccountClear}
                            />
                        }
                    />
                    <Route path="/submitted/*" element={<CreatePasswordView />} />
                    <Route
                        path="/"
                        element={<Navigate to={`/welcome?${searchParams.toString()}`} />}
                    />
                    <Route path="/welcome" element={<WelcomePageWithSplit />} />
                </Routes>
            </LeadDataProvider>
        </AccountProvider>
    );
};

export const App = () => {
    // Create Split config with dynamic user key
    const userKey = getUserKey();
    const splitConfigWithUser = {
        ...splitConfig,
        core: {
            ...splitConfig.core,
            key: userKey,
        },
    };

    return (
        <SplitFactoryProvider config={splitConfigWithUser}>
            <AppWithSplit />
            <Analytics mode={process.env.REACT_APP_ENV === 'prod' ? 'production' : 'development'} />
            <SpeedInsights debug={process.env.REACT_APP_ENV === 'idev' ? true : false} />
        </SplitFactoryProvider>
    );
};
